import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useFirebase } from "@/contexts/FirebaseContext";
import { collection, query, where, getDocs, addDoc, getFirestore, Timestamp, doc, getDoc, updateDoc } from "firebase/firestore";
import { format } from "date-fns";
import { Calendar, Clock, User, Search, Book } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

// Buffer time configuration - 15 minutes between lessons for instructor break
const LESSON_BUFFER_MINUTES = 15;

interface Enrollment {
  id: string;
  userId: string;
  courseName: string;
  courseType: string;
  courseSubtype?: string;
  totalHours: number;
  hoursTaken: number;
  hoursRemaining: number;
  vehicleType?: string;
  licenseType?: string;
}

interface Student {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  enrollments: Enrollment[];
}

const InstructorBookLesson = () => {
  const { currentUser } = useFirebase();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment | null>(null);
  const [isBookingDialogOpen, setIsBookingDialogOpen] = useState(false);
  const [bookingData, setBookingData] = useState({
    date: "",
    startTime: "",
    duration: "1",
    venue: "",
    notes: "",
  });
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  const [availableTimeSlots, setAvailableTimeSlots] = useState<string[]>([]);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);

  // Generate time slots in 15-minute intervals
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const formattedHour = hour.toString().padStart(2, '0');
        const formattedMinute = minute.toString().padStart(2, '0');
        slots.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  useEffect(() => {
    const fetchAssignedStudents = async () => {
      try {
        setLoading(true);
        const db = getFirestore();
        
        // Get enrollments where this instructor is assigned
        const enrollmentsQuery = query(
          collection(db, "enrollments"),
          where("instructorId", "==", currentUser?.uid)
        );
        
        const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
        const enrollments = enrollmentsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Enrollment[];

        // Group enrollments by student
        const studentEnrollments = new Map<string, Enrollment[]>();
        enrollments.forEach(enrollment => {
          const existing = studentEnrollments.get(enrollment.userId) || [];
          studentEnrollments.set(enrollment.userId, [...existing, enrollment]);
        });

        // Get student details for each enrollment
        const studentsData = await Promise.all(
          Array.from(studentEnrollments.entries()).map(async ([userId, studentEnrollments]) => {
            const studentDoc = await getDocs(
              query(collection(db, "users"), where("id", "==", userId))
            );
            const studentData = studentDoc.docs[0]?.data();
            
            return {
              id: userId,
              firstName: studentData?.firstName || "",
              lastName: studentData?.lastName || "",
              email: studentData?.email || "",
              phone: studentData?.phone || "",
              enrollments: studentEnrollments
            };
          })
        );

        setStudents(studentsData);
        setFilteredStudents(studentsData);
      } catch (error) {
        console.error("Error fetching students:", error);
        toast({
          title: "Error",
          description: "Failed to load students. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    if (currentUser) {
      fetchAssignedStudents();
    }
  }, [currentUser, toast]);

  useEffect(() => {
    const filtered = students.filter(
      student =>
        `${student.firstName} ${student.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredStudents(filtered);
  }, [searchTerm, students]);

  const handleStudentSelect = (student: Student) => {
    setSelectedStudent(student);
    setSelectedEnrollment(null);
    setIsBookingDialogOpen(true);
  };

  const handleBooking = async () => {
    if (!selectedStudent || !selectedEnrollment) return;

    try {
      const isAvailable = await checkAvailability(
        bookingData.date,
        bookingData.startTime,
        parseInt(bookingData.duration)
      );

      if (!isAvailable) {
        return;
      }

      const db = getFirestore();
      
      // Get student details
      const studentQuery = query(
        collection(db, "users"),
        where("id", "==", selectedStudent.id)
      );
      const studentSnapshot = await getDocs(studentQuery);
      const studentData = studentSnapshot.docs[0]?.data();

      if (!studentData) {
        throw new Error("Student data not found");
      }

      const studentName = `${studentData.firstName} ${studentData.lastName}`;
      const duration = parseInt(bookingData.duration);
      const startTime = bookingData.startTime;
      const [startHours, startMinutes] = startTime.split(':').map(Number);
      const startMinutesTotal = startHours * 60 + startMinutes;
      const endMinutesTotal = startMinutesTotal + (duration * 60);
      const endHours = Math.floor(endMinutesTotal / 60);
      const endMinutes = endMinutesTotal % 60;
      const endTime = `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;

      // Update enrollment hours
      const enrollmentRef = doc(db, "enrollments", selectedEnrollment.id);
      const enrollmentDoc = await getDoc(enrollmentRef);
      const enrollmentData = enrollmentDoc.exists() ? enrollmentDoc.data() : null;

      if (enrollmentData) {
        const newHoursTaken = (enrollmentData.hoursTaken || 0) + duration;
        const newHoursRemaining = (enrollmentData.totalHours || 0) - newHoursTaken;

        await updateDoc(enrollmentRef, {
          hoursTaken: newHoursTaken,
          hoursRemaining: newHoursRemaining,
          lastLessonDate: bookingData.date
        });
      }

      const newBooking = {
        instructorId: currentUser?.uid,
        instructorEmail: currentUser?.email,
        instructorName: currentUser?.displayName || '',
        instructorNotified: true,
        enrollmentId: selectedEnrollment.id,
        studentId: selectedStudent.id,
        studentName: studentName,
        studentEmail: studentData.email,
        studentPhone: studentData.phone || '',
        studentAddress: studentData.address || '',
        studentPostcode: studentData.postcode || '',
        studentGearbox: studentData.gearbox || '',
        courseName: selectedEnrollment.courseName,
        courseType: selectedEnrollment.courseType,
        courseSubtype: selectedEnrollment.courseSubtype || '',
        date: bookingData.date,
        startTime: startTime,
        endTime: endTime,
        startMinutes: startMinutesTotal,
        endMinutes: endMinutesTotal,
        duration: duration,
        venue: bookingData.venue,
        notes: bookingData.notes,
        status: "confirmed",
        createdAt: new Date().toISOString(),
        totalHours: selectedEnrollment.totalHours || 0,
        hoursTaken: selectedEnrollment.hoursTaken || 0,
        hoursRemaining: selectedEnrollment.hoursRemaining || 0,
        vehicleType: selectedEnrollment.vehicleType || 'Not specified',
        licenseType: selectedEnrollment.licenseType || 'Not specified'
      };

      await addDoc(collection(db, "schedules"), newBooking);

      toast({
        title: "Success",
        description: "Lesson booked successfully!",
      });

      setIsBookingDialogOpen(false);
      setSelectedStudent(null);
      setSelectedEnrollment(null);
      setBookingData({
        date: "",
        startTime: "",
        duration: "1",
        venue: "",
        notes: "",
      });
    } catch (error) {
      console.error("Error booking lesson:", error);
      toast({
        title: "Error",
        description: "Failed to book lesson. Please try again.",
        variant: "destructive",
      });
    }
  };

  const checkAvailability = async (date: string, startTime: string, duration: number) => {
    setIsCheckingAvailability(true);
    setAvailabilityError("");
    
    try {
      const db = getFirestore();
      const startDateTime = new Date(`${date}T${startTime}`);
      const endDateTime = new Date(startDateTime.getTime() + duration * 60 * 60 * 1000);

      console.log("=== INSTRUCTOR BOOKING AVAILABILITY CHECK ===");
      console.log("Checking availability for date:", date);
      console.log("Start time:", startTime);
      console.log("Duration:", duration, "hours");

      // Check instructor's unavailability from instructor_availability collection
      const availabilityRef = doc(db, 'instructor_availability', currentUser?.email || '');
      const availabilityDoc = await getDoc(availabilityRef);
      
      if (availabilityDoc.exists()) {
        const unavailableSlots = availabilityDoc.data().unavailableSlots || [];
        console.log("Found unavailable slots:", unavailableSlots);
        
        // Check if the selected date has any unavailability
        const dateUnavailableSlots = unavailableSlots.filter((slot: any) => slot.date === date);
        console.log("Unavailable slots for selected date:", dateUnavailableSlots);
        
        for (const slot of dateUnavailableSlots) {
          if (slot.isFullDay) {
            console.log("Instructor is unavailable for the entire day");
            setAvailabilityError("Instructor is unavailable for the entire day");
            return false;
          }

          const slotStart = new Date(`${date}T${slot.startTime}`);
          const slotEnd = new Date(`${date}T${slot.endTime}`);
          
          if (
            (startDateTime >= slotStart && startDateTime < slotEnd) ||
            (endDateTime > slotStart && endDateTime <= slotEnd) ||
            (startDateTime <= slotStart && endDateTime >= slotEnd)
          ) {
            console.log("Time slot conflicts with instructor unavailability:", slot.startTime, "-", slot.endTime);
            setAvailabilityError(`Instructor is unavailable from ${slot.startTime} to ${slot.endTime}`);
            return false;
          }
        }
      } else {
        console.log("No availability document found for instructor");
      }

      // Check student's existing bookings
      if (selectedStudent) {
        const studentBookingsQuery = query(
          collection(db, "schedules"),
          where("studentId", "==", selectedStudent.id),
          where("date", "==", date)
        );
        
        const studentBookingsSnapshot = await getDocs(studentBookingsQuery);
        const studentBookings = studentBookingsSnapshot.docs.map(doc => doc.data());

        console.log("Student's existing bookings:", studentBookings);

        for (const booking of studentBookings) {
          const bookingStart = new Date(`${date}T${booking.startTime}`);
          const bookingEnd = new Date(bookingStart.getTime() + booking.duration * 60 * 60 * 1000);
          
          if (
            (startDateTime >= bookingStart && startDateTime < bookingEnd) ||
            (endDateTime > bookingStart && endDateTime <= bookingEnd) ||
            (startDateTime <= bookingStart && endDateTime >= bookingEnd)
          ) {
            console.log("Student already has a booking during this time");
            setAvailabilityError("Student already has a booking during this time");
            return false;
          }
        }
      }

      // Check instructor's existing bookings
      const instructorBookingsQuery = query(
        collection(db, "schedules"),
        where("instructorId", "==", currentUser?.uid),
        where("date", "==", date)
      );
      
      const instructorBookingsSnapshot = await getDocs(instructorBookingsQuery);
      const instructorBookings = instructorBookingsSnapshot.docs.map(doc => doc.data());

      console.log("Instructor's existing bookings:", instructorBookings);

      for (const booking of instructorBookings) {
        const bookingStart = new Date(`${date}T${booking.startTime}`);
        const bookingEnd = new Date(bookingStart.getTime() + booking.duration * 60 * 60 * 1000);
        // Add buffer time for instructor break - 15 minutes after each lesson
        const bookingEndWithBuffer = new Date(bookingEnd.getTime() + (LESSON_BUFFER_MINUTES * 60 * 1000));
        
        if (
          (startDateTime >= bookingStart && startDateTime < bookingEndWithBuffer) ||
          (endDateTime > bookingStart && endDateTime <= bookingEndWithBuffer) ||
          (startDateTime <= bookingStart && endDateTime >= bookingEndWithBuffer)
        ) {
          console.log("Instructor already has a booking during this time (including buffer)");
          setAvailabilityError("You already have a booking during this time. Please allow 15-minute buffer time between lessons.");
          return false;
        }
      }

      console.log("Availability check passed");
      return true;
    } catch (error) {
      console.error("Error checking availability:", error);
      setAvailabilityError("Error checking availability. Please try again.");
      return false;
    } finally {
      setIsCheckingAvailability(false);
    }
  };

  const getAvailableTimeSlots = async (date: string) => {
    if (!date || !currentUser?.uid) {
      setAvailableTimeSlots([]);
      return;
    }

    try {
      setIsLoadingTimeSlots(true);
      const db = getFirestore();
      
      // Get instructor's bookings for the day
      const instructorBookingsQuery = query(
        collection(db, "schedules"),
        where("instructorId", "==", currentUser.uid),
        where("date", "==", date),
        where("status", "in", ["pending", "confirmed", "completed"])
      );
      const instructorBookingsSnapshot = await getDocs(instructorBookingsQuery);
      const instructorBookings = instructorBookingsSnapshot.docs.map(doc => doc.data());

      // Get instructor's unavailability
      const availabilityRef = doc(db, 'instructor_availability', currentUser.email || '');
      const availabilityDoc = await getDoc(availabilityRef);
      const unavailableSlots = availabilityDoc.exists() ? availabilityDoc.data().unavailableSlots || [] : [];
      const dateUnavailableSlots = unavailableSlots.filter((slot: any) => slot.date === date);

      // Create a set of unavailable time slots
      const unavailableTimes = new Set();

      // Add booked times to the set
      instructorBookings.forEach(booking => {
        if (booking.status === 'cancelled') return; // Skip cancelled lessons
        
        const startTime = booking.startTime;
        const duration = booking.duration || 1;
        const [hours, minutes] = startTime.split(':').map(Number);
        const startMinutes = hours * 60 + minutes;
        const endMinutes = startMinutes + (duration * 60);

        for (let m = startMinutes; m < endMinutes; m += 15) {
          const h = Math.floor(m / 60);
          const min = m % 60;
          unavailableTimes.add(`${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
        }
      });

      // Add unavailable times to the set
      dateUnavailableSlots.forEach((slot: any) => {
        if (slot.isFullDay) {
          // If full day unavailable, all times are blocked
          timeSlots.forEach(time => unavailableTimes.add(time));
        } else {
          const [startHours, startMinutes] = slot.startTime.split(':').map(Number);
          const [endHours, endMinutes] = slot.endTime.split(':').map(Number);
          const startTotalMinutes = startHours * 60 + startMinutes;
          const endTotalMinutes = endHours * 60 + endMinutes;

          for (let m = startTotalMinutes; m < endTotalMinutes; m += 15) {
            const h = Math.floor(m / 60);
            const min = m % 60;
            unavailableTimes.add(`${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`);
          }
        }
      });

      // Filter out unavailable times
      const availableSlots = timeSlots.filter(timeSlot => !unavailableTimes.has(timeSlot));

      console.log("Available time slots:", availableSlots);
      setAvailableTimeSlots(availableSlots);
    } catch (error) {
      console.error("Error getting available time slots:", error);
      // Fallback to all time slots if there's an error
      setAvailableTimeSlots(timeSlots);
    } finally {
      setIsLoadingTimeSlots(false);
    }
  };

  const handleDateChange = (date: string) => {
    setBookingData({ ...bookingData, date, startTime: "" });
    getAvailableTimeSlots(date);
  };

  if (loading) {
    return (
      <DashboardLayout role="instructor">
        <div className="flex justify-center items-center h-64">
          <p>Loading students...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="instructor">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Book Lesson for Student</h1>
          <p className="text-gray-500 mt-2">Book lessons on behalf of your assigned students</p>
        </div>

        <div className="flex items-center space-x-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input
              type="search"
              placeholder="Search students..."
              className="pl-8"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="grid gap-4">
          {filteredStudents.map((student) => (
            <Card key={student.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-semibold text-lg">
                      {student.firstName} {student.lastName}
                    </h3>
                    <p className="text-gray-500">{student.email}</p>
                    <div className="mt-2 space-y-1">
                      {student.enrollments.map((enrollment) => (
                        <div key={enrollment.id} className="text-sm">
                          <p>
                            <span className="font-medium">Course:</span> {enrollment.courseName}
                          </p>
                          <p>
                            <span className="font-medium">Progress:</span> {enrollment.hoursTaken}/{enrollment.totalHours} hours
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button
                    onClick={() => handleStudentSelect(student)}
                  >
                    Book Lesson
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Dialog open={isBookingDialogOpen} onOpenChange={setIsBookingDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Book Lesson</DialogTitle>
              <DialogDescription>
                Book a lesson for {selectedStudent?.firstName} {selectedStudent?.lastName}
              </DialogDescription>
            </DialogHeader>

            <div className="grid gap-4 py-4">
              {!selectedEnrollment && (
                <div className="space-y-2">
                  <Label>Select Course</Label>
                  <Select
                    onValueChange={(value) => {
                      const enrollment = selectedStudent?.enrollments.find(e => e.id === value);
                      setSelectedEnrollment(enrollment || null);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a course" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedStudent?.enrollments.map((enrollment) => (
                        <SelectItem key={enrollment.id} value={enrollment.id}>
                          {enrollment.courseName} ({enrollment.hoursTaken}/{enrollment.totalHours} hours)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {selectedEnrollment && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="date">Date</Label>
                      <Input
                        id="date"
                        type="date"
                        value={bookingData.date}
                        onChange={(e) => handleDateChange(e.target.value)}
                        min={format(new Date(), "yyyy-MM-dd")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="startTime">Start Time</Label>
                      <Select
                        value={bookingData.startTime}
                        onValueChange={(value) => setBookingData({ ...bookingData, startTime: value })}
                        disabled={isLoadingTimeSlots || !bookingData.date}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={
                            isLoadingTimeSlots 
                              ? "Loading available times..." 
                              : !bookingData.date 
                                ? "Select date first" 
                                : "Select time"
                          } />
                        </SelectTrigger>
                        <SelectContent>
                          {availableTimeSlots.length === 0 && !isLoadingTimeSlots ? (
                            <SelectItem value="no-slots" disabled>
                              No available time slots
                            </SelectItem>
                          ) : (
                            availableTimeSlots.map((time) => (
                              <SelectItem key={time} value={time}>
                                {time}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="duration">Duration (hours)</Label>
                    <Select
                      value={bookingData.duration}
                      onValueChange={(value) => setBookingData({ ...bookingData, duration: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 1.5, 2, 2.5, 3].map((duration) => (
                          <SelectItem key={duration} value={duration.toString()}>
                            {duration} {duration === 1 ? "hour" : "hours"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="venue">Venue</Label>
                    <Input
                      id="venue"
                      value={bookingData.venue}
                      onChange={(e) => setBookingData({ ...bookingData, venue: e.target.value })}
                      placeholder="Enter meeting location"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Notes</Label>
                    <Input
                      id="notes"
                      value={bookingData.notes}
                      onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                      placeholder="Any special instructions or notes"
                    />
                  </div>
                </>
              )}

              {availabilityError && (
                <div className="text-sm text-red-500 mt-2">
                  {availabilityError}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsBookingDialogOpen(false);
                setSelectedStudent(null);
                setSelectedEnrollment(null);
              }}>
                Cancel
              </Button>
              {selectedEnrollment && (
                <Button 
                  onClick={handleBooking}
                  disabled={isCheckingAvailability}
                >
                  {isCheckingAvailability ? "Checking..." : "Book Lesson"}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default InstructorBookLesson; 